import React, { createContext, useContext } from 'react';

import { ArticleNavContextValue } from './embeds/blocks/ArticleNavContext';
import { KnzNode } from './types';

/**
 * Toc（目次）ブロックの描画モード。
 * - inline: 従来どおり本文中に目次を描画する（デフォルト。後方互換）
 * - hoist: 本文中には描画せず、目次を右カラム等へ「昇格」させる（記事公開ページ用）
 * - ignore: 目次を一切描画しない（エディタ等のプレビュー用）
 */
export type TocMode = 'inline' | 'hoist' | 'ignore';

type KnzDocumentContextType = {
  /** ドキュメントのルートノード（Toc 等が全体構造を参照するため） */
  roots: KnzNode[];
  /** Toc ブロックの描画モード */
  tocMode: TocMode;
  /**
   * 記事リンク（ArticleLinkCard）等の遷移コンテキスト。
   * ArticleNav が上下スロットで受け取る navContext と同じ意味だが、こちらは本文フロー中の
   * ブロックが参照できるようにドキュメント全体で共有する。
   * - 'preview'（既定・ワークスペースのプレビュー）: リンクは新規タブで開く
   * - 'article'（記事詳細）: 通常のルーティング遷移
   */
  navContext: ArticleNavContextValue;
};

const KnzDocumentContext = createContext<KnzDocumentContextType | null>(null);

/** Toc などドキュメント全体構造を参照するコンポーネント用フック */
export const useKnzDocument = (): KnzDocumentContextType => {
  const ctx = useContext(KnzDocumentContext);
  if (!ctx) {
    // ビューワー外で使われた場合は空扱い（クラッシュさせない）
    return { roots: [], tocMode: 'inline', navContext: 'preview' };
  }
  return ctx;
};

export const KnzDocumentProvider: React.FC<{
  roots: KnzNode[];
  tocMode?: TocMode;
  navContext?: ArticleNavContextValue;
  children: React.ReactNode;
}> = ({ roots, tocMode = 'inline', navContext = 'preview', children }) => {
  const value = React.useMemo(() => ({ roots, tocMode, navContext }), [roots, tocMode, navContext]);
  return <KnzDocumentContext.Provider value={value}>{children}</KnzDocumentContext.Provider>;
};
